import React, { useState, useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import PackagingOptionsDialog from '../components/PackagingOptionsDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Package, 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  Loader2,
  AlertCircle,
  Upload,
  ChevronDown,
  FileText,
  Lock,
  AlertTriangle,
  CheckSquare,
  X,
  Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import DuplicateProductsModal from '../components/DuplicateProductsModal';
import InvoiceProductImportDialog from '../components/InvoiceProductImportDialog';
import MultiProductImportDialog from '../components/MultiProductImportDialog';

const AdminProductsPage = () => {
  const { api, user } = useAuth();
  const { isTrialExpired } = useTrialStatus();
  
  // Mark this page as visited
  useMarkPageVisited('/admin/products');
  
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [rayons, setRayons] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [rayonFilter, setRayonFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [duplicatesModalOpen, setDuplicatesModalOpen] = useState(false);
  const [invoiceImportOpen, setInvoiceImportOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [multiImportOpen, setMultiImportOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [packagingDialogOpen, setPackagingDialogOpen] = useState(false);
  const [packagingUnits, setPackagingUnits] = useState([]);
  const [packagingTypes, setPackagingTypes] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    article: '',
    product_type: '',
    rayon: '',
    
    // NOUVEAU SYSTÈME : Conditionnement fournisseur
    supplier_packaging_quantity: 0,
    supplier_packaging_unit: 'KG',
    supplier_packaging_type: 'colis',
    supplier_package_price: '',
    
    // NOUVEAU SYSTÈME : Détaillabilité
    is_divisible: false,
    divisible_quantity: 1,
    divisible_unit: 'KG',
    
    // Prix catalogue (calculé automatiquement)
    catalog_price: '',
    catalog_unit: '',
    
    // ANCIEN SYSTÈME (gardé pour compatibilité temporaire)
    order_unit: '',
    min_quantity: '1',
    price_description: '',
    unit_price: '',
    
    supplier_id: '',
    is_available: true
  });


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes, rayonsRes, typesRes, packagingRes] = await Promise.allSettled([
        api.get('/products'),
        api.get('/suppliers/all'),
        api.get('/rayons'),
        api.get('/types'),
        api.get('/packaging-options')
      ]);
      if (productsRes.status === 'fulfilled') {
        setProducts(productsRes.value.data);
      } else {
        console.error('Failed to fetch products:', productsRes.reason);
        toast.error('Erreur lors du chargement des produits');
      }
      if (suppliersRes.status === 'fulfilled') {
        setSuppliers(suppliersRes.value.data);
      } else {
        console.error('Failed to fetch suppliers:', suppliersRes.reason);
      }
      if (rayonsRes.status === 'fulfilled') {
        setRayons(rayonsRes.value.data);
      } else {
        console.error('Failed to fetch rayons:', rayonsRes.reason);
      }
      if (typesRes.status === 'fulfilled') {
        setTypes(typesRes.value.data);
      } else {
        console.error('Failed to fetch types:', typesRes.reason);
      }
      if (packagingRes.status === 'fulfilled') {
        setPackagingUnits(packagingRes.value.data.units || []);
        setPackagingTypes(packagingRes.value.data.types || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.article.toLowerCase().includes(search.toLowerCase()) ||
      product.code.toLowerCase().includes(search.toLowerCase());
    const matchesSupplier = supplierFilter === 'all' || product.supplier_id === supplierFilter;
    const matchesRayon = rayonFilter === 'all' || product.rayon === rayonFilter;
    const matchesType = typeFilter === 'all' || product.product_type === typeFilter;
    return matchesSearch && matchesSupplier && matchesRayon && matchesType;
  });

  const getRayonName = (rayonId) => {
    const rayon = rayons.find(r => r.id === rayonId);
    return rayon ? rayon.nom : rayonId;
  };

  const getTypeName = (typeId) => {
    const type = types.find(t => t.id === typeId);
    return type ? type.nom : typeId;
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        code: product.code,
        article: product.article,
        product_type: product.product_type || '',
        rayon: product.rayon || '',
        dlc: product.dlc || false,
        
        // NOUVEAU SYSTÈME
        supplier_packaging_quantity: product.supplier_packaging_quantity || 0,
        supplier_packaging_unit: product.supplier_packaging_unit || 'KG',
        supplier_packaging_type: product.supplier_packaging_type || 'colis',
        supplier_package_price: product.supplier_package_price ? product.supplier_package_price.toString() : '',
        is_divisible: product.is_divisible || false,
        divisible_quantity: product.divisible_quantity || 1,
        divisible_unit: product.divisible_unit || 'KG',
        catalog_price: product.catalog_price ? product.catalog_price.toString() : '',
        catalog_unit: product.catalog_unit || '',
        
        // ANCIEN SYSTÈME (compatibilité)
        order_unit: product.order_unit || '',
        min_quantity: product.min_quantity || '1',
        price_description: product.price_description || '',
        unit_price: product.unit_price ? product.unit_price.toString() : '',
        
        supplier_id: product.supplier_id,
        is_available: product.is_available
      });
    } else {
      setEditingProduct(null);
      setFormData({
        code: '',
        article: '',
        product_type: '',
        rayon: '',
        
        // NOUVEAU SYSTÈME
        supplier_packaging_quantity: 0,
        supplier_packaging_unit: 'KG',
        supplier_packaging_type: 'colis',
        supplier_package_price: '',
        is_divisible: false,
        divisible_quantity: 1,
        divisible_unit: 'KG',
        catalog_price: '',
        catalog_unit: '',
        
        // ANCIEN SYSTÈME
        order_unit: '',
        min_quantity: '1',
        price_description: '',
        unit_price: '',
        
        supplier_id: '',
        is_available: true
      });
    }
    setDialogOpen(true);
  };

  // Auto-recalcul du prix catalogue basé sur le conditionnement
  const recalculateCatalogPrice = (field, value, currentFormData) => {
    const updated = { ...currentFormData, [field]: value };
    
    // Ne pas recalculer si on modifie directement catalog_price ou catalog_unit
    if (field === 'catalog_price' || field === 'catalog_unit') {
      return updated;
    }
    
    const qty = parseFloat(updated.supplier_packaging_quantity) || 0;
    const packagePrice = parseFloat(updated.supplier_package_price) || 0;
    
    if (updated.is_divisible && qty > 0 && packagePrice > 0) {
      // Si détaillable : prix au détail
      updated.catalog_price = (packagePrice / qty).toFixed(2);
      updated.catalog_unit = updated.divisible_unit || updated.supplier_packaging_unit || '';
    } else if (packagePrice > 0) {
      // Si non détaillable : prix du conditionnement entier
      updated.catalog_price = packagePrice.toFixed(2);
      updated.catalog_unit = updated.supplier_packaging_type || '';
    }
    
    return updated;
  };

  const handleFormDataChange = (field, value) => {
    setFormData(prev => recalculateCatalogPrice(field, value, prev));
  };

  const handleSubmit = async () => {
    // Validation des champs obligatoires (au minimum code, article, supplier_id)
    if (!formData.code || !formData.article || !formData.supplier_id) {
      toast.error('Veuillez remplir au minimum : Code, Article et Fournisseur');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        code: formData.code,
        article: formData.article,
        product_type: formData.product_type || '',
        rayon: formData.rayon || '',
        dlc: formData.dlc || false,
        
        // NOUVEAU SYSTÈME
        supplier_packaging_quantity: parseFloat(formData.supplier_packaging_quantity) || null,
        supplier_packaging_unit: formData.supplier_packaging_unit || null,
        supplier_packaging_type: formData.supplier_packaging_type || null,
        supplier_package_price: formData.supplier_package_price ? parseFloat(formData.supplier_package_price) : null,
        is_divisible: formData.is_divisible || false,
        divisible_quantity: parseFloat(formData.divisible_quantity) || null,
        divisible_unit: formData.divisible_unit || null,
        catalog_price: formData.catalog_price ? parseFloat(formData.catalog_price) : null,
        catalog_unit: formData.catalog_unit || null,
        
        // ANCIEN SYSTÈME (compatibilité)
        order_unit: formData.order_unit || '',
        min_quantity: formData.min_quantity || '1',
        price_description: formData.price_description || '',
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : (formData.catalog_price ? parseFloat(formData.catalog_price) : null),
        
        supplier_id: formData.supplier_id,
        is_available: formData.is_available
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success('Produit modifié');
      } else {
        await api.post('/products', payload);
        toast.success('Produit créé');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      // Gérer les erreurs de validation Pydantic qui peuvent être un tableau d'objets
      let errorMessage = 'Erreur lors de l\'opération';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si c'est un tableau d'erreurs de validation
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Si c'est une chaîne
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Si c'est un objet
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Produit supprimé');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const handleDeleteAllProducts = async () => {
    setIsDeletingAll(true);
    try {
      const response = await api.delete('/products');
      toast.success(response.data.message || 'Tous les produits ont été supprimés');
      setDeleteAllDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    } finally {
      setIsDeletingAll(false);
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleProductSelection = (productId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const allFilteredSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id));

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const response = await api.delete('/products/bulk', {
        data: { product_ids: Array.from(selectedIds) }
      });
      toast.success(response.data.message || 'Produits supprimés');
      setBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      setSelectionMode(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleToggleAvailability = async (product) => {
    try {
      await api.put(`/products/${product.id}`, {
        ...product,
        is_available: !product.is_available
      });
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, is_available: !p.is_available } : p
      ));
      toast.success(product.is_available ? 'Produit désactivé' : 'Produit activé');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in relative" data-testid="admin-products-page">
      {isTrialExpired && <TrialExpiredOverlay />}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Produits</h1>
          <p className="text-gray-500 mt-1">Gestion du catalogue produits</p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Créez et mettez à jour votre catalogue. Importez vos produits depuis une facture ou un devis, ou saisissez-les manuellement.
          </p>
          <p className="text-sm font-semibold text-[#1B4332] mt-2" data-testid="products-count">
            {filteredProducts.length === products.length
              ? `${products.length} produit${products.length > 1 ? 's' : ''} au total`
              : `${filteredProducts.length} produit${filteredProducts.length > 1 ? 's' : ''} affiché${filteredProducts.length > 1 ? 's' : ''} sur ${products.length} au total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                data-testid="import-dropdown-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importer
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setMultiImportOpen(true)} data-testid="multi-import-btn">
                <Plus className="w-4 h-4 mr-2" />
                Multi produits
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setInvoiceImportOpen(true)} data-testid="import-new-btn">
                <FileText className="w-4 h-4 mr-2" />
                Import facture ou devis
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {products.length > 0 && (
            <Button
              variant="outline"
              onClick={toggleSelectionMode}
              className={selectionMode
                ? 'border-[#1B4332] bg-[#D8F3DC] text-[#1B4332] hover:bg-[#B7E4C7]'
                : 'border-gray-300'}
              data-testid="toggle-selection-mode-btn"
            >
              {selectionMode ? (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Sélectionner
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setPackagingDialogOpen(true)}
            className="border-gray-300"
            data-testid="open-packaging-options-btn"
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Conditionnements
          </Button>
          <Button
            variant="outline"
            onClick={() => setDuplicatesModalOpen(true)}
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Détecter doublons
          </Button>
          
          {/* Bouton danger - Supprimer tout */}
          {user?.is_admin && products.length > 0 && (
            <Button
              onClick={() => setDeleteAllDialogOpen(true)}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 hover:text-red-700"
              data-testid="delete-all-products-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer tout
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Rechercher par code ou article..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="products-search-input"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="products-type-filter">
            <SelectValue placeholder="Tous les types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {types.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.nom}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={rayonFilter} onValueChange={setRayonFilter}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="products-rayon-filter">
            <SelectValue placeholder="Tous les rayons" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rayons</SelectItem>
            {rayons.map((rayon) => (
              <SelectItem key={rayon.id} value={rayon.id}>
                {rayon.nom}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="products-supplier-filter">
            <SelectValue placeholder="Tous les fournisseurs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les fournisseurs</SelectItem>
            {suppliers.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectionMode && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 p-3 bg-[#D8F3DC] border border-[#1B4332]/30 rounded-lg shadow-sm" data-testid="selection-toolbar">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={toggleSelectAllFiltered}
              className="border-[#1B4332] data-[state=checked]:bg-[#1B4332]"
              data-testid="select-all-checkbox"
            />
            <button
              type="button"
              onClick={toggleSelectAllFiltered}
              className="text-sm font-medium text-[#1B4332] hover:underline"
              data-testid="select-all-label"
            >
              {allFilteredSelected ? 'Tout désélectionner' : `Tout sélectionner (${filteredProducts.length})`}
            </button>
          </div>
          <span className="text-sm text-gray-700" data-testid="selected-count">
            {selectedIds.size} produit{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>
          <Button
            size="sm"
            disabled={selectedIds.size === 0}
            onClick={() => setBulkDeleteDialogOpen(true)}
            className="ml-auto bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer la sélection ({selectedIds.size})
          </Button>
        </div>
      )}

      <div className="grid gap-4">
        {filteredProducts.map((product) => (
          <Card 
            key={product.id} 
            className={`border ${selectionMode && selectedIds.has(product.id)
              ? 'border-[#1B4332] ring-1 ring-[#1B4332] bg-[#D8F3DC]/30'
              : product.is_available ? 'border-gray-200' : 'border-red-200 bg-red-50/50'} ${selectionMode ? 'cursor-pointer' : ''}`}
            onClick={selectionMode ? () => toggleProductSelection(product.id) : undefined}
            data-testid={`product-card-${product.code}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectionMode && (
                    <Checkbox
                      checked={selectedIds.has(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="border-[#1B4332] data-[state=checked]:bg-[#1B4332]"
                      data-testid={`select-product-${product.code}`}
                    />
                  )}
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${product.is_available ? 'bg-[#D8F3DC]' : 'bg-red-100'}`}>
                    <Package className={`w-6 h-6 ${product.is_available ? 'text-[#1B4332]' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="font-mono text-xs">
                        {product.code}
                      </Badge>
                      {product.product_type && (
                        <Badge variant="secondary" className="text-xs">
                          {getTypeName(product.product_type)}
                        </Badge>
                      )}
                      {product.rayon && (
                        <Badge className="text-xs bg-[#D8F3DC] text-[#1B4332]">
                          {getRayonName(product.rayon)}
                        </Badge>
                      )}
                      {!product.is_available && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Indisponible
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900">{product.article}</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      {/* Affichage du nouveau système de conditionnement */}
                      {product.supplier_packaging_quantity && product.is_divisible !== undefined ? (
                        <div className="flex flex-col gap-0.5">
                          <p className="flex items-center gap-1.5">
                            <span className="text-gray-500 text-xs">Vendu:</span>
                            <span className="font-medium">
                              {product.supplier_packaging_quantity} × {product.supplier_packaging_unit} par {product.supplier_packaging_type}
                            </span>
                            {product.supplier_package_price && (
                              <span className="text-gray-400 text-xs">
                                ({product.supplier_package_price.toFixed(2)}€)
                              </span>
                            )}
                          </p>
                          {product.is_divisible ? (
                            <p className="flex items-center gap-1">
                              <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-green-500 text-white text-xs">✓</span>
                              <span className="text-xs text-gray-600">
                                Détaillable par {product.divisible_quantity || 1} {product.divisible_unit}
                              </span>
                            </p>
                          ) : (
                            <p className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">Non détaillable</span>
                            </p>
                          )}
                        </div>
                      ) : (
                        // Ancien système (compatibilité)
                        <p>
                          {product.order_unit ? (
                            <>
                              <span className="font-medium">{product.order_unit}</span>
                              {product.min_quantity && product.min_quantity !== '1' && (
                                <span className="text-gray-500"> • Min: {product.min_quantity}</span>
                              )}
                            </>
                          ) : (
                            <>{product.min_packaging} • {product.unit}</>
                          )}
                        </p>
                      )}
                      <p className="text-xs">{product.supplier_name}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4" onClick={(e) => { if (selectionMode) e.stopPropagation(); }}>
                  <div className="text-right">
                    {/* Afficher catalog_price du nouveau système si disponible, sinon unit_price */}
                    <p className="font-bold text-lg tabular-nums">
                      {(product.catalog_price || product.unit_price || 0).toFixed(2)} €
                    </p>
                    {product.catalog_unit ? (
                      <p className="text-xs text-gray-500">/ {product.catalog_unit}</p>
                    ) : product.price_description ? (
                      <p className="text-xs text-gray-600">{product.price_description}</p>
                    ) : (
                      <p className="text-xs text-gray-500">/ {product.order_unit || product.unit}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={product.is_available}
                      onCheckedChange={() => handleToggleAvailability(product)}
                      data-testid={`toggle-availability-${product.code}`}
                    />
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => handleOpenDialog(product)}
                      data-testid={`edit-product-${product.code}-btn`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="outline"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(product.id)}
                      data-testid={`delete-product-${product.code}-btn`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Aucun produit trouvé</h3>
          <p className="text-gray-500">Ajoutez votre premier produit</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Modifiez les informations du produit' : 'Ajoutez un nouveau produit au catalogue'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto px-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code produit *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="font-mono"
                  placeholder="REF123"
                  data-testid="product-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier">Fournisseur *</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                >
                  <SelectTrigger data-testid="product-supplier-select">
                    <SelectValue placeholder="Sélectionner un fournisseur" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="article">Article *</Label>
              <Input
                id="article"
                value={formData.article}
                onChange={(e) => setFormData({ ...formData, article: e.target.value })}
                placeholder="Nom du produit"
                data-testid="product-article-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product_type">Type</Label>
                <Select 
                  value={formData.product_type} 
                  onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                >
                  <SelectTrigger data-testid="product-type-select">
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rayon">Rayon</Label>
                <Select 
                  value={formData.rayon} 
                  onValueChange={(value) => setFormData({ ...formData, rayon: value })}
                >
                  <SelectTrigger data-testid="product-rayon-select">
                    <SelectValue placeholder="Sélectionner un rayon" />
                  </SelectTrigger>
                  <SelectContent>
                    {rayons.map((rayon) => (
                      <SelectItem key={rayon.id} value={rayon.id}>
                        {rayon.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Switch
                checked={Boolean(formData.dlc)}
                onCheckedChange={(checked) => setFormData({ ...formData, dlc: checked })}
                className="data-[state=checked]:bg-amber-500"
                data-testid="product-dlc-switch"
              />
              <div>
                <Label className="text-sm font-medium">Produit avec DLC</Label>
                <p className="text-xs text-gray-500">Date Limite de Consommation à suivre (produits périssables)</p>
              </div>
            </div>

            <div className="space-y-4 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-purple-600" />
                <Label className="text-base font-semibold text-purple-900">Conditionnement & Commande</Label>
              </div>
              
              {/* LIGNE 1 : Conditionnement fournisseur */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">
                  Conditionnement fournisseur
                  <span className="block text-xs text-gray-500 font-normal mt-1">
                    Comment le fournisseur vend ce produit ?
                  </span>
                </Label>
                <div className="flex items-center gap-2 bg-white p-3 rounded-lg">
                  <span className="text-sm text-gray-600 font-medium">Vendu :</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.supplier_packaging_quantity?.toString() || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        handleFormDataChange('supplier_packaging_quantity', numValue);
                      } else if (value === '' || value === '.') {
                        handleFormDataChange('supplier_packaging_quantity', 0);
                      }
                    }}
                    className="h-10 w-20 text-center"
                    placeholder="10"
                    data-testid="product-supplier-qty-input"
                  />
                  <span className="text-sm text-gray-600">×</span>
                  <Select
                    value={formData.supplier_packaging_unit || 'KG'}
                    onValueChange={(value) => handleFormDataChange('supplier_packaging_unit', value)}
                  >
                    <SelectTrigger className="h-10 w-24" data-testid="product-supplier-unit-select">
                      <SelectValue placeholder="KG" />
                    </SelectTrigger>
                    <SelectContent>
                      {packagingUnits.map((u) => (
                        <SelectItem key={u.id} value={u.value}>{u.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-600">par</span>
                  <Select
                    value={formData.supplier_packaging_type || 'colis'}
                    onValueChange={(value) => handleFormDataChange('supplier_packaging_type', value)}
                  >
                    <SelectTrigger className="h-10 w-28" data-testid="product-supplier-type-select">
                      <SelectValue placeholder="colis" />
                    </SelectTrigger>
                    <SelectContent>
                      {packagingTypes.map((t) => (
                        <SelectItem key={t.id} value={t.value}>{t.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* LIGNE 2 : Prix du conditionnement */}
              <div className="space-y-2">
                <Label htmlFor="supplier_package_price" className="text-sm font-semibold text-gray-700">
                  Prix du conditionnement (€ HT)
                  <span className="block text-xs text-gray-500 font-normal mt-1">
                    Prix payé au fournisseur pour 1 {formData.supplier_packaging_type || 'colis'}
                  </span>
                </Label>
                <div className="flex items-center gap-2 bg-white p-3 rounded-lg">
                  <Input
                    id="supplier_package_price"
                    type="text"
                    inputMode="decimal"
                    value={formData.supplier_package_price}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      handleFormDataChange('supplier_package_price', value);
                    }}
                    className="h-10 text-right"
                    placeholder="0.00"
                    data-testid="product-supplier-price-input"
                  />
                  <span className="text-sm text-gray-600 font-medium">€</span>
                </div>
              </div>

              {/* LIGNE 3 : Détaillable */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white p-3 rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="is_divisible" className="text-sm font-semibold text-gray-700 cursor-pointer">
                      Produit détaillable ?
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Peut-on commander en quantité inférieure au conditionnement ?
                    </p>
                  </div>
                  <Switch
                    id="is_divisible"
                    checked={formData.is_divisible}
                    onCheckedChange={(checked) => handleFormDataChange('is_divisible', checked)}
                    className="data-[state=checked]:bg-green-600"
                    data-testid="product-is-divisible-switch"
                  />
                </div>

                {formData.is_divisible && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200 space-y-2">
                    <Label className="text-sm font-semibold text-green-900">
                      Unité de commande détaillable
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Commandable par</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={formData.divisible_quantity?.toString() || '1'}
                        onChange={(e) => {
                          const value = e.target.value.replace(',', '.');
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue)) {
                            handleFormDataChange('divisible_quantity', numValue);
                          } else if (value === '' || value === '.') {
                            handleFormDataChange('divisible_quantity', 1);
                          }
                        }}
                        className="h-9 w-20 text-center bg-white"
                        placeholder="1"
                        data-testid="product-divisible-qty-input"
                      />
                      <Select
                        value={formData.divisible_unit || 'KG'}
                        onValueChange={(value) => handleFormDataChange('divisible_unit', value)}
                      >
                        <SelectTrigger className="h-9 w-24 bg-white" data-testid="product-divisible-unit-select">
                          <SelectValue placeholder="KG" />
                        </SelectTrigger>
                        <SelectContent>
                          {packagingUnits.map((u) => (
                            <SelectItem key={u.id} value={u.value}>{u.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              {/* LIGNE 4 : Prix catalogue (calculé automatiquement, modifiable) */}
              <div className="space-y-2">
                <Label htmlFor="catalog_price" className="text-sm font-semibold text-gray-700">
                  Prix catalogue
                  <span className="block text-xs text-gray-500 font-normal mt-1">
                    Prix affiché au catalogue (calculé automatiquement, modifiable)
                  </span>
                </Label>
                <div className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-200">
                  <Input
                    id="catalog_price"
                    type="text"
                    inputMode="decimal"
                    value={formData.catalog_price}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      handleFormDataChange('catalog_price', value);
                    }}
                    className="h-10 text-right font-bold bg-white"
                    placeholder="0.00"
                    data-testid="product-catalog-price-input"
                  />
                  <span className="text-sm text-gray-600 font-medium">€</span>
                  <span className="text-sm text-gray-600">/</span>
                  <span className="text-sm font-medium text-gray-700">
                    {formData.catalog_unit || '...'}
                  </span>
                </div>
                {formData.is_divisible && formData.supplier_packaging_quantity > 0 && formData.supplier_package_price && (
                  <p className="text-xs text-blue-700 italic">
                    💡 Calculé auto : {formData.supplier_package_price} € ÷ {formData.supplier_packaging_quantity} = {(parseFloat(formData.supplier_package_price) / parseFloat(formData.supplier_packaging_quantity)).toFixed(2)} € / {formData.divisible_unit}
                  </p>
                )}
              </div>
              
              <div className="text-xs text-purple-700 bg-purple-100 p-3 rounded-lg">
                💡 <strong>Astuce :</strong> Le prix catalogue est automatiquement calculé selon votre conditionnement. Vous pouvez le modifier manuellement si nécessaire.
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_available">Disponible à la commande</Label>
              <Switch
                id="is_available"
                checked={formData.is_available}
                onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                data-testid="product-available-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={formLoading}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
              data-testid="save-product-btn"
            >
              {formLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog de confirmation pour la suppression en masse */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Supprimer {selectedIds.size} produit{selectedIds.size > 1 ? 's' : ''} ?
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="text-gray-700 font-medium">
                ⚠️ Cette action est <span className="text-red-600 font-bold">IRRÉVERSIBLE</span>
              </p>
              <p className="text-gray-600">
                Vous êtes sur le point de supprimer <span className="font-semibold text-gray-900">{selectedIds.size} produit{selectedIds.size > 1 ? 's' : ''}</span> de votre catalogue.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={isBulkDeleting}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-bulk-delete-btn"
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Oui, supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-products Dialog */}
      {/* Dialog de confirmation pour supprimer tous les produits */}
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Supprimer tous les produits ?
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="text-gray-700 font-medium">
                ⚠️ Cette action est <span className="text-red-600 font-bold">IRRÉVERSIBLE</span>
              </p>
              <p className="text-gray-600">
                Vous êtes sur le point de supprimer <span className="font-semibold text-gray-900">{products.length} produits</span> de votre catalogue.
              </p>
              <p className="text-sm text-gray-500">
                Cette fonctionnalité est utile pour repartir de zéro lors de l'onboarding ou après des tests.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteAllDialogOpen(false)}
              disabled={isDeletingAll}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteAllProducts}
              disabled={isDeletingAll}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-all-btn"
            >
              {isDeletingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Oui, supprimer tout
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Modale Import multi-produits (saisie manuelle unifiée) */}
      <MultiProductImportDialog
        open={multiImportOpen}
        onOpenChange={setMultiImportOpen}
        suppliers={suppliers}
        onImportSuccess={fetchData}
      />

      {/* Modale Détection Doublons */}
      <DuplicateProductsModal
        open={duplicatesModalOpen}
        onOpenChange={setDuplicatesModalOpen}
        onDeleted={() => {
          fetchData(); // Refresh products list
        }}
      />

      {/* Modale Import New — produits depuis facture PDF (vision) */}
      <InvoiceProductImportDialog
        open={invoiceImportOpen}
        onOpenChange={setInvoiceImportOpen}
        suppliers={suppliers}
        onImportSuccess={() => {
          fetchData();
        }}
      />

      {/* Modale Gestion des conditionnements (unités + types) */}
      <PackagingOptionsDialog
        open={packagingDialogOpen}
        onOpenChange={setPackagingDialogOpen}
        api={api}
        onChanged={fetchData}
      />

    </div>
  );
};

export default AdminProductsPage;
